"use client";
import type { Media } from "@mw/shared/payload-types";

import { Image } from "@components/core/Image";
import { RichText } from "@components/core/RichText";
import { SliderGeneric } from "@ui/components/composers/SliderGeneric";
import Muted from "@ui/icons/muted.svg";
import Play from "@ui/icons/play.svg";
import SoundOn from "@ui/icons/sound_on.svg";
import { ImageSizes } from "@ui/lib/image";
import { useInView } from "@ui/lib/useInView";
import { LoaderCircleIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface VideoSliderItem {
  description: string;
  placeholder: Media;
  videoUrl: string;
}

interface VideoSliderItemProps {
  description: string;
  isActiveAudio: boolean;
  isAnotherAudioActive: boolean;
  isHovered: boolean;
  onCardClick: () => void;
  onHover: () => void;
  onLeave: () => void;
  placeholder: Media;
  videoUrl: string;
}

// VideoSliderItem component
function VideoSliderItemComponent({
  videoUrl,
  placeholder,
  description,
  isActiveAudio,
  isHovered,
  onHover,
  onLeave,
  isAnotherAudioActive,
  onCardClick,
}: VideoSliderItemProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isTouch, setIsTouch] = useState(false);
  const inViewRef = useRef<HTMLButtonElement>(null);
  const inView = useInView(inViewRef, { threshold: 0.2, once: false });
  const initialVolume = useRef(0);

  // Show placeholder if not hovered/active, or if video not loaded/playing
  const shouldShowVideo = isActiveAudio || isHovered || hasStartedPlaying;
  const showPlaceholder = !shouldShowVideo || !isVideoLoaded || !isVideoPlaying;

  // Detect if device is touchscreen
  useEffect(() => {
    setIsTouch(window.matchMedia("(pointer: coarse)").matches);
  }, []);

  const fadeOut = useCallback(async (duration = 300) => {
    if (videoRef.current) {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
      const video = videoRef.current;
      const steps = 30;
      const stepDuration = duration / steps;
      const volumeStep = video.volume / steps;
      let currentVolume = video.volume;
      initialVolume.current = video.volume;
      for (let i = 0; i < steps; i++) {
        await new Promise(resolve => {
          fadeTimeoutRef.current = setTimeout(() => {
            currentVolume -= volumeStep;
            video.volume = Math.max(currentVolume, 0);
            resolve(true);
          }, i * stepDuration);
        });
      }
      video.volume = 0;
    }
  }, []);

  useEffect(() => {
    if (!inView && isActiveAudio) {
      fadeOut().then(() => {
        onCardClick();
      });
    }
  }, [inView, isActiveAudio, onCardClick, fadeOut]);

  // Play/pause logic
  useEffect(() => {
    if (videoRef.current) {
      if (isActiveAudio) {
        videoRef.current.muted = false;
        setMuted(false);
        if (videoRef.current.volume === 0) {
          videoRef.current.volume = initialVolume.current;
        }
        videoRef.current.play();
      } else if (isHovered && !isAnotherAudioActive) {
        videoRef.current.muted = true;
        setMuted(true);
        if (videoRef.current.volume === 0) {
          videoRef.current.volume = initialVolume.current;
        }
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActiveAudio, isHovered, isAnotherAudioActive]);

  // Determine button state and icon
  const buttonState = useMemo(() => {
    if (!isVideoLoaded) {
      return { icon: null, isLoading: true };
    }

    if (isActiveAudio) {
      // This video is playing with audio: show mute/unmute
      return {
        icon: muted ? (
          <Muted className='text-white h-20 w-20 cursor-pointer' />
        ) : (
          <SoundOn className='h-20 w-20 cursor-pointer' />
        ),
        isLoading: false,
      };
    }

    if (isAnotherAudioActive && isHovered) {
      // Another video is playing with audio, and this is hovered: show play button
      return {
        icon: <Play className='h-20 w-20 cursor-pointer' />,
        isLoading: false,
      };
    }

    if (isHovered && !isAnotherAudioActive) {
      // This video is hovered, no other video is playing with audio: show unmute button
      return {
        icon: <Muted className='h-20 w-20 cursor-pointer' />,
        isLoading: false,
      };
    }

    return { icon: null, isLoading: false };
  }, [isVideoLoaded, isActiveAudio, muted, isAnotherAudioActive, isHovered]);

  return (
    <>
      <div
        className='slider-image bg-gray-100 relative mb-20 w-full overflow-hidden rounded-lg transition-transform duration-300 ease-in-out active:scale-[0.99]'
        style={{ aspectRatio: "6/7" }}
      >
        <button
          aria-label='Play video'
          className='group flex h-full w-full cursor-pointer flex-col'
          onClick={onCardClick}
          onMouseEnter={!isTouch ? onHover : undefined}
          onMouseLeave={!isTouch ? onLeave : undefined}
          ref={inViewRef}
        >
          <div className='absolute h-full w-full'>
            {placeholder.url && (
              <Image
                alt={placeholder.alt || ""}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-in-out ${showPlaceholder ? "opacity-100" : "opacity-0"}`}
                fit='cover'
                quality={100}
                sizes={ImageSizes.FULL_1_3_ON_LG}
                src={placeholder.url}
              />
            )}
          </div>
          {shouldShowVideo && (
            <>
              <video
                autoPlay
                className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${showPlaceholder ? "opacity-0" : "opacity-100"}`}
                muted={muted}
                onLoadedData={() => setIsVideoLoaded(true)}
                onPlay={() => {
                  setIsVideoPlaying(true);
                  setHasStartedPlaying(true);
                }}
                playsInline
                poster={placeholder.url || undefined}
                preload='none'
                ref={videoRef}
                src={videoUrl}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                tabIndex={-1}
              />
              {(buttonState.icon || buttonState.isLoading) && (
                <div
                  className={`video-slider-control absolute bottom-16 left-16 z-10 flex h-40 w-40 items-center justify-center rounded-full border border-overlay-darker backdrop-blur-md ${isActiveAudio && !muted ? "bg-inverse text-inverse" : "bg-[#EAE5E1]/80 text-overlay-darker-plus"}`}
                >
                  {buttonState.isLoading ? (
                    <LoaderCircleIcon className='animate-spin' size={20} />
                  ) : buttonState.icon ? (
                    buttonState.icon
                  ) : null}
                </div>
              )}
            </>
          )}
        </button>
      </div>
      <RichText color='gray' content={description} withHeadings={false} />
    </>
  );
}

interface VideoSliderProps {
  items: VideoSliderItem[];
  itemsPerViewportAtLg?: 3 | 4;
}

export function VideoSlider({ items, itemsPerViewportAtLg = 3 }: VideoSliderProps) {
  const [activeAudioIndex, setActiveAudioIndex] = useState<null | number>(null);
  const [hoveredIndex, setHoveredIndex] = useState<null | number>(null);

  const handleCardClick = (idx: number) => {
    if (activeAudioIndex !== idx) {
      setActiveAudioIndex(idx);
    } else {
      // Mute
      setActiveAudioIndex(null);
    }
  };

  const sliderItems = items.map((item, idx) => (
    <VideoSliderItemComponent
      description={item.description}
      isActiveAudio={activeAudioIndex === idx}
      isAnotherAudioActive={activeAudioIndex !== null && activeAudioIndex !== idx}
      isHovered={hoveredIndex === idx}
      key={idx}
      onCardClick={() => handleCardClick(idx)}
      onHover={() => setHoveredIndex(idx)}
      onLeave={() => setHoveredIndex(null)}
      placeholder={item.placeholder}
      videoUrl={item.videoUrl}
    />
  ));

  return (
    <SliderGeneric
      centeredAnimation
      floatingControls
      imageSelector='.slider-image'
      inContainer={false}
      items={sliderItems}
      itemsPerViewportAtLg={itemsPerViewportAtLg}
    />
  );
}
